import i18n from '@/constants/i18n';

/**
 * Geographic coordinate interface
 */
export interface GeographicPoint {
  latitude: number;
  longitude: number;
}

/**
 * Countries that use miles as their primary unit of distance measurement
 * This includes countries like the US, UK, and some others
 */
const MILES_COUNTRIES = new Set([
  'US', // United States
  'GB', // United Kingdom
  'MM', // Myanmar
  'LR', // Liberia
]);

/**
 * Conversion factor from miles to kilometers
 */
const MILES_TO_KM = 1.60934;

/**
 * Determines if a given locale uses miles as the primary distance unit
 * @param locale - The locale to check (e.g., 'en-US', 'en-GB', 'es-ES')
 * @returns true if the locale uses miles, false if it uses kilometers
 */
export function usesMiles(locale?: string): boolean {
  const currentLocale = locale || i18n.language;

  // Extract country code from locale (e.g., 'en-US' -> 'US')
  const countryCode = currentLocale.split('-')[1]?.toUpperCase();

  // If we have a country code, check if it's in our miles countries list
  if (countryCode) {
    return MILES_COUNTRIES.has(countryCode);
  }

  // If no country code, check the language code
  const languageCode = currentLocale.split('-')[0]?.toLowerCase();

  // Default to kilometers for most languages, except English
  // English-speaking countries are more likely to use miles
  return languageCode === 'en';
}

/**
 * Converts miles to kilometers if the user's locale uses kilometers
 * @param miles - The distance in miles
 * @param locale - Optional locale override (uses current i18n locale if not provided)
 * @returns The distance in the appropriate unit for the user's locale
 */
export function convertMilesToUserUnit(miles: number, locale?: string): number {
  if (usesMiles(locale)) {
    return miles;
  }

  return miles * MILES_TO_KM;
}

/**
 * Formats a distance value with appropriate unit based on user locale
 * @param miles - The distance in miles
 * @param locale - Optional locale override (uses current i18n locale if not provided)
 * @param precision - Number of decimal places (default: 1)
 * @returns Formatted distance string with unit
 */
export function formatDistance(
  miles: number,
  locale?: string,
  precision: number = 1,
): string {
  const value = convertMilesToUserUnit(miles, locale) ?? 0;
  const unit = usesMiles(locale) ? 'mi' : 'km';

  return `${value.toFixed(precision)} ${unit}`;
}

/**
 * Gets the appropriate distance unit for the current locale
 * @param locale - Optional locale override (uses current i18n locale if not provided)
 * @returns The distance unit ('mi' for miles, 'km' for kilometers)
 */
export function getDistanceUnit(locale?: string): 'mi' | 'km' {
  return usesMiles(locale) ? 'mi' : 'km';
}

/**
 * Converts miles to kilometers
 * @param miles - The distance in miles
 * @returns The distance in kilometers
 */
export function milesToKilometers(miles: number): number {
  return miles * MILES_TO_KM;
}

/**
 * Converts kilometers to miles
 * @param kilometers - The distance in kilometers
 * @returns The distance in miles
 */
export function kilometersToMiles(kilometers: number): number {
  return kilometers / MILES_TO_KM;
}
